/**
 * Section 4 — when the post goes out.
 *
 * The three modes are a radio group rather than a dropdown because they are not
 * variations on one action: "publish now" is irreversible, "schedule" is
 * reversible until it fires, and "draft" publishes nothing. Showing all three at
 * once is what stops the irreversible one being picked by accident.
 */

import { useTranslation } from '../../../context/LanguageContext';
import { DateTimePicker } from './DateTimePicker';
import { cn } from '../../../lib/utils';
import type { ScheduleMode, ScheduleOptions } from '../../../types/composer';
import type { TranslationKey } from '../../../i18n';

const MODES: { mode: ScheduleMode; labelKey: TranslationKey }[] = [
  { mode: 'now', labelKey: 'composer.schedule.now' },
  { mode: 'scheduled', labelKey: 'composer.schedule.schedule' },
  { mode: 'draft', labelKey: 'composer.schedule.draft' },
];

interface ScheduleSectionProps {
  schedule: ScheduleOptions;
  onChange: (schedule: ScheduleOptions) => void;
}

export function ScheduleSection({ schedule, onChange }: Readonly<ScheduleSectionProps>) {
  const { t } = useTranslation();

  return (
    <div>
      <div className="flex flex-wrap items-center gap-x-7 gap-y-2">
        {MODES.map(({ mode, labelKey }) => (
          <label
            key={mode}
            className="flex cursor-pointer items-center gap-2 text-[13px] text-slate-700"
          >
            <input
              type="radio"
              name="schedule-mode"
              checked={schedule.mode === mode}
              onChange={() => onChange({ ...schedule, mode })}
              className="h-4 w-4 border-slate-300 text-blue-600 accent-blue-600 focus:ring-blue-500"
            />
            <span className={cn(schedule.mode === mode && 'font-semibold text-slate-900')}>
              {t(labelKey)}
            </span>
          </label>
        ))}
      </div>

      {schedule.mode === 'scheduled' && (
        <div className="mt-3 flex flex-wrap items-end gap-3 rounded-lg border border-slate-200 bg-slate-50/70 p-3">
          <div className="flex-1 min-w-[200px]">
            <span className="mb-1 block text-[11px] font-semibold text-slate-600">
              {t('composer.schedule.dateTime')}
            </span>
            <DateTimePicker
              value={schedule.scheduledAt}
              onChange={(scheduledAt) => onChange({ ...schedule, scheduledAt })}
            />
          </div>

          <div className="min-w-[160px]">
            <span className="mb-1 block text-[11px] font-semibold text-slate-600">
              {t('composer.schedule.timezone')}
            </span>
            <p className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-[13px] text-slate-500">
              {schedule.timezone}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
